import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/authApi';
import { setAuthToken } from '../utils/authTokenStore';
import { setModeratorApiKey, hydrateModeratorApiKeyFromSession, readModeratorApiKeyFromToken } from '../utils/apiClient';

function getStoredModeratorId() {
  try {
    const v = localStorage.getItem('hawk_moderator_id');
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

function setStoredModeratorId(id) {
  try {
    if (id != null) {
      localStorage.setItem('hawk_moderator_id', String(id));
    } else {
      localStorage.removeItem('hawk_moderator_id');
    }
  } catch { /* ignore */ }
}

const AuthContext = createContext();

const VALID_ROLES = new Set(['admin', 'moderator', 'subscriber']);

function normalizeStoredUser(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const role = parsed.role && VALID_ROLES.has(parsed.role) ? parsed.role : 'admin';
  return {
    email: parsed.email ?? '',
    displayName: parsed.displayName ?? parsed.email ?? 'User',
    role,
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moderatorId, setModeratorId] = useState(() => getStoredModeratorId());

  // Avoid race condition after login: `token` state updates asynchronously,
  // but we persist the token immediately to localStorage.
  const storedToken = (() => {
    try {
      return localStorage.getItem('hawk_admin_token');
    } catch {
      return null;
    }
  })();

  const isAuthenticated = !!token || !!storedToken;

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await authApi.login(email, password);
      const accessToken = data.access_token ?? data.token ?? data.accessToken;
      if (!accessToken) {
        throw new Error('No access token in login response');
      }
      const apiRole = data.user?.role ?? data.role;
      const role =
        apiRole && VALID_ROLES.has(String(apiRole).toLowerCase())
          ? String(apiRole).toLowerCase()
          : 'admin';
      const userData = {
        email: data.user?.email ?? email,
        displayName: data.user?.name ?? data.user?.email ?? email,
        role,
      };
      setTokenState(accessToken);
      setUser(userData);
      setAuthToken(accessToken);
      localStorage.setItem('hawk_admin_token', accessToken);
      localStorage.setItem('hawk_admin_user', JSON.stringify(userData));
      if (role === 'moderator') {
        const apiKey =
          (typeof data.api_key === 'string' && data.api_key.trim())
          || readModeratorApiKeyFromToken(accessToken);
        if (apiKey) setModeratorApiKey(apiKey);
        const mid = data.moderator_id ?? null;
        setModeratorId(mid);
        setStoredModeratorId(mid);
      } else {
        setModeratorApiKey(null);
        setModeratorId(null);
        setStoredModeratorId(null);
      }
      return { success: true, role };
    } catch (err) {
      const message = err.response?.data?.detail ?? err.response?.data?.message ?? err.message ?? 'Login failed';
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setTokenState(null);
    setUser(null);
    setAuthToken(null);
    setModeratorApiKey(null);
    setModeratorId(null);
    setStoredModeratorId(null);
    localStorage.removeItem('hawk_admin_token');
    localStorage.removeItem('hawk_admin_user');
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('hawk_admin_token');
    const storedUser  = localStorage.getItem('hawk_admin_user');
    if (storedToken) {
      setTokenState(storedToken);
      setAuthToken(storedToken);
      if (storedUser) {
        try {
          const parsedUser = normalizeStoredUser(JSON.parse(storedUser));
          setUser(parsedUser);
          if (parsedUser?.role === 'moderator') {
            hydrateModeratorApiKeyFromSession();
          } else {
            setModeratorApiKey(null);
          }
        } catch {
          setUser({ email: 'Admin', displayName: 'Admin', role: 'admin' });
          setModeratorApiKey(null);
        }
      } else {
        setUser({ email: 'Admin', displayName: 'Admin', role: 'admin' });
        setModeratorApiKey(null);
      }
    }
    setLoading(false);
  }, []);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    /** Numeric moderator account ID — required for /moderators/{id}/scheduler endpoints */
    moderatorId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
